import { isCustomLayoutCartItem } from './designUpload';
import {
  isProfileSoldOut,
  layoutProfiles,
  productOfLayout,
} from './customizerLayout';

// The saved-design lists' logic, out of MyDesignsScreen and CommunityDesignsScreen (backfill
// PR 32): which layouts of the keycap-set products are sold out for which profiles and
// whether a listed design sits on one (both screens held a copy of each), the cart item a
// listed design becomes (both screens held one; My Designs' marks a custom layout and
// Community Designs' leaves the mark out, which a parameter keeps), My Designs' rename
// check and the public-status change its share menu requests. Since chain 10 (Oct 2026) also
// the cards' owner and uploaded-icons checks (three copies, with the cart item's) and the
// signed thumbnails the two lists merge in (My Designs and the admin's design list).

export const findSoldOutLayouts = (layouts) =>
  // Create a Set of sold-out profiles for each layout
  new Set(
    layouts
      .map((layout) => {
        const soldOutProfiles = layoutProfiles(layout).filter((profile) =>
          isProfileSoldOut(layout, profile)
        ); // the profiles the layout has no stock in (the customizer's rule, since a chain 8 review)

        if (soldOutProfiles.length > 0) {
          return { layoutId: layout._id, soldOutProfiles }; // Include layoutId and sold-out profiles
        }

        return null; // Exclude layouts with no sold-out profiles
      })
      .filter(Boolean) // Remove null entries
  ); // the layouts with a profile out of stock, as the lists' reducers keep them

export const isDesignSoldOut = (soldOutLayouts, design) =>
  Array.from(soldOutLayouts).some(
    (layout) =>
      layout.layoutId === design.layoutEditor.id &&
      layout.soldOutProfiles.includes(design.profileEditor.profile)
  ); // checks if this design's selected layout/profile is sold out (a scan of the reducers' Set of objects, as the screens did; a Map keyed by layout would be a redesign of the state: left as is, user decision, Oct 2026)

export const ownsDesign = (userInfo, design) =>
  userInfo && String(design?.metadata?.userId) === String(userInfo.userId); // checks if the signed-in user is the creator/owner of this design (null or false when nobody is signed in, as the cards' ternaries read it)

export const purchaseDisabled = (design, userInfo) =>
  design?.metadata?.containsVisibleUserUploadedIcons === true &&
  !ownsDesign(userInfo, design); // disables purchase for other users if this design contains visible user-uploaded icons (the cards of My Designs and Community Designs, and the cart item below)

export const designsWithThumbnails = (designs, thumbnails) =>
  designs.map((design) => ({
    ...design,
    metadata: {
      ...design.metadata,
      signedThumbnailUrl: thumbnails[design._id] || '',
    },
  })); // the listed designs with the signed thumbnail links POST /api/designs/thumbnails answered (its map always comes), '' for a design the route left out: one hard-deleted between the two requests, or, in My Designs' liked and purchased tabs, another user's design turned private or soft-deleted meanwhile (the admin's list fell back to the stored link until chain 10, Oct 2026, which the deleted design's bucket object no longer answers either: user decision)

export const savedDesignCartItem = (
  design,
  products,
  userInfo,
  { withCustomLayoutMark = true } = {}
) => {
  if (purchaseDisabled(design, userInfo)) {
    return { reason: 'uploaded-icons' }; // prevent adding designs with uploaded icons to the cart unless the signed-in user owns those uploaded icons
  }

  const matchedProduct = productOfLayout(products, design.layoutEditor.id); // Find the matching product

  if (!matchedProduct) {
    return { reason: 'no-product' };
  }

  return {
    item: {
      ...matchedProduct,
      quantity: 1,
      designId: design._id,
      keycapProfile: design.profileEditor.profile,
      thumbnail: design.metadata.thumbnail,
      ...(withCustomLayoutMark && {
        isCustomLayout: isCustomLayoutCartItem(design, matchedProduct), // mark whether this design is based on a custom layout (My Designs; Community Designs leaves the mark out, as it did before PR 32: user decision)
      }),
    },
  };
}; // the cart item a listed design becomes, or the reason it can't (each screen shows its own message for a reason)

export const renameCheck = (design, name) => {
  if (design.metadata.publicStatus !== 'private') {
    return { reason: 'not-private' }; // If it's not private, don't let them rename (the name field and its menu item are disabled for every other status, so nothing reaches this; kept as a guard, user decision, Oct 2026)
  }

  if (!name.trim()) {
    // make sure the name value is not only spaces
    return { reason: 'empty' };
  }

  if (design.name === name.trim()) {
    // .trim() remove spaces before the first letter and after the last one
    return { reason: 'unchanged' };
  }

  return { name: name.trim() };
}; // what My Designs does with a typed name: the trimmed name to save, or the reason it saves nothing

export const publicStatusChange = (publicStatus) => {
  if (publicStatus === 'private') {
    // Case 1: Request publishing the design
    return {
      publicStatus: 'pending',
      messageKey: 'my-designs-screen-request-submitted',
    };
  }

  if (publicStatus === 'pending') {
    // Case 2: Withdraw the request
    return {
      publicStatus: 'private',
      messageKey: 'my-designs-screen-request-withdrawn',
    };
  }

  if (publicStatus === 'public') {
    // Case 3: Revert back to private
    return {
      publicStatus: 'private',
      messageKey: 'my-designs-screen-design-set-private',
    };
  }

  return null; // Case 4: No action for rejected designs (the share menu hides its item for them, so nothing reaches this; kept, user decision, Oct 2026)
}; // the status the share menu's item moves a design to, with the key of the message to show, or null for a rejected design
